import { StreamChat } from 'stream-chat';
import { axiosInstance } from '../clients/axiosClient';
import { API_V1_PREFIX } from '../utils/constants';

// Stream Chat API Key from environment variables
// Note: In your .env file, remove quotes and spaces: REACT_APP_STREAM_CHAT_API_KEY=sydghmskzvnc
export const STREAM_API_KEY = process.env.REACT_APP_STREAM_CHAT_API_KEY || process.env.REACT_APP_STREAM_API_KEY || 'sydghmskzvnc';

// Stream Chat API Secret from environment variables
// ⚠️ WARNING: This is only used as fallback when backend token endpoint fails
// In production, tokens should be generated server-side
const STREAM_API_SECRET = process.env.REACT_APP_STREAM_CHAT_API_SECRET || '';

const CHAT_API_PREFIX = 'chat/';

let streamClient: any | null = null;

export const getStreamClient = () => {
  if (!streamClient) {
    streamClient = new StreamChat(STREAM_API_KEY);
  }
  return streamClient;
};

/**
 * Generate a JWT token for Stream Chat (CLIENT-SIDE FALLBACK ONLY)
 * ⚠️ WARNING: Only use this when backend token endpoint is unavailable
 * 
 * @param {string} userId - The user ID for the token
 * @returns {Promise<string>} JWT token for Stream Chat
 * @throws {Error} If STREAM_API_SECRET is not configured
 */
export const generateToken = async (userId: string): Promise<string> => {
  if (!userId) {
    throw new Error('User ID is required to generate token');
  }

  if (!STREAM_API_SECRET) {
    throw new Error(
      'REACT_APP_STREAM_CHAT_API_SECRET is not set in environment variables. ' +
      'Please configure it in your .env file or use the backend token endpoint (getChatToken).'
    );
  }

  const base64UrlEncode = (str: string): string => {
    return btoa(str)
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '');
  };

  const header = {
    alg: 'HS256',
    typ: 'JWT',
  };

  // Create payload with 24 hour expiration
  const now = Math.floor(Date.now() / 1000);
  const payload = {
    user_id: userId,
    exp: now + 60 * 60 * 24, // 24 hours
    iat: now,
  };

  const encodedHeader = base64UrlEncode(JSON.stringify(header));
  const encodedPayload = base64UrlEncode(JSON.stringify(payload));

  const message = `${encodedHeader}.${encodedPayload}`;
  const encoder = new TextEncoder();

  try {
    const key = await crypto.subtle.importKey(
      'raw',
      encoder.encode(STREAM_API_SECRET),
      { name: 'HMAC', hash: 'SHA-256' },
      false,
      ['sign']
    );
    
    const signature = await crypto.subtle.sign('HMAC', key, encoder.encode(message));
    const digest = Array.from(new Uint8Array(signature))
      .map((b) => String.fromCharCode(b))
      .join('');
    const encodedSignature = base64UrlEncode(digest);
    
    return `${message}.${encodedSignature}`;
  } catch (error) {
    console.error('Error generating JWT token:', error);
    throw new Error('Failed to generate token. Please use backend token endpoint.');
  }
};

export interface StreamUser {
  id: string;
  name?: string;
  role?: string;
  image?: string;
}

/**
 * Connect a user to Stream Chat with a provided token
 * ⚠️ Note: Prefer using server-generated tokens via getChatToken()
 * 
 * @param {string} userId - The user ID
 * @param {StreamUser} [userData] - Optional user data (name, role, image)
 * @param {string} token - The authentication token (from backend or generateToken)
 * @returns {Promise<boolean>} Success status
 */
export const connectUser = async (
  userId: string,
  userData: StreamUser | undefined,
  token: string
): Promise<boolean> => {
  try {
    if (!userId || !token) {
      console.error('User ID and token are required to connect');
      return false;
    }

    const client = getStreamClient();
    await client.connectUser(
      {
        id: userId,
        name: userData?.name || userId,
        role: userData?.role,
        image: userData?.image,
      },
      token
    );
    return true;
  } catch (error) {
    console.error('Error connecting user to Stream Chat:', error);
    return false;
  }
};

export const disconnectUser = async (): Promise<void> => {
  try {
    const client = getStreamClient();
    await client.disconnectUser();
  } catch (error) {
    console.error('Error disconnecting from Stream Chat:', error);
  }
};

export const getOrCreateChannel = async (
  client: any,
  memberId: string,
  providerId: string,
  providerRole: 'doctor' | 'nutritionist' | 'support',
  memberName?: string,
  providerName?: string
) => {
  try {
    if (!client || !client.userID) {
      throw new Error('Client must be connected before creating a channel');
    }
    
    const channelId = `${providerRole}-${providerId}`;
    
    const channel = client.channel('messaging', channelId, {
      members: [memberId, providerId],
      created_by_id: memberId,
      name: getChannelName(providerRole),
      data: {
        memberId,
        providerId,
        providerRole,
        memberName,
        providerName,
      },
    });
    
    await channel.watch();
    return channel;
  } catch (error) {
    console.error('Error creating/getting channel:', error);
    throw error;
  }
};

const getChannelName = (providerRole: 'doctor' | 'nutritionist' | 'support'): string => {
  switch (providerRole) {
    case 'doctor':
      return 'Doctor';
    case 'nutritionist':
      return 'Nutritionist';
    case 'support':
      return 'Support';
    default:
      return 'Chat';
  }
};

export const queryChannels = async (
  filters: any,
  sort: any = { last_message_at: -1 }
) => {
  try {
    const client = getStreamClient();
    const filter = { type: 'messaging', ...filters };
    
    return await client.queryChannels(filter, sort, {
      watch: true,
      state: true,
    });
  } catch (error) {
    console.error('Error querying channels:', error);
    throw error;
  }
};

export const markChannelAsRead = async (channelId: string, userId: string) => {
  try {
    const client = getStreamClient();
    const channel = client.channel('messaging', channelId);
    
    if (!channel.initialized) {
      await channel.watch();
    }
    
    await channel.markRead();
  } catch (error) {
    console.error('Error marking channel as read:', error);
  }
};

export const getUnreadCount = (channels: any[]): number => {
  return channels.reduce((total, channel) => {
    return total + (channel.state?.unreadCount || 0);
  }, 0);
};

export const getChatToken = async (): Promise<{ token: string; user_id: string }> => {
  try {
    const { data } = await axiosInstance.get(`${API_V1_PREFIX}${CHAT_API_PREFIX}token`);
    return data;
  } catch (error: any) {
    console.error('Error getting chat token:', error);
    throw error;
  }
};

export const getMemberChats = async (): Promise<any> => {
  try {
    const { data } = await axiosInstance.get(`${API_V1_PREFIX}${CHAT_API_PREFIX}my-chats`);
    return data;
  } catch (error: any) {
    console.error('Error getting member chats:', error);
    throw error;
  }
};

export const getDoctorChats = async (): Promise<any> => {
  try {
    const { data } = await axiosInstance.get(`${API_V1_PREFIX}${CHAT_API_PREFIX}doctor/chats`);
    return data;
  } catch (error: any) {
    console.error('Error getting doctor chats:', error);
    throw error;
  }
};

export const getNutritionistChats = async (): Promise<any> => {
  try {
    const { data } = await axiosInstance.get(`${API_V1_PREFIX}${CHAT_API_PREFIX}nutritionist/chats`);
    return data;
  } catch (error: any) {
    console.error('Error getting nutritionist chats:', error);
    throw error;
  }
};

export const getAdminChats = async (
  chat_type?: 'doctors' | 'nutritionists' | 'admins',
  name?: string
): Promise<any> => {
  try {
    const searchParams = new URLSearchParams();
    if (chat_type) {
      searchParams.set('chat_type', chat_type);
    }
    const trimmedName = (name ?? '').trim();
    if (trimmedName.length > 0) {
      searchParams.set('name', trimmedName);
    }

    const qs = searchParams.toString();
    const { data } = await axiosInstance.get(
      `${API_V1_PREFIX}${CHAT_API_PREFIX}admin/chats${qs ? `?${qs}` : ''}`
    );
    return data;
  } catch (error: any) {
    console.error('Error getting admin chats:', error);
    throw error;
  }
};

export interface UnreadCounts {
  total_unread_doctors: number;
  total_unread_nutritionists: number;
  total_unread_support: number;
}

export const getUnreadCounts = async (): Promise<UnreadCounts> => {
  try {
    const { data } = await axiosInstance.get(`${API_V1_PREFIX}${CHAT_API_PREFIX}unread-counts`);
    return data;
  } catch (error: any) {
    console.error('Error getting unread counts:', error);
    return {
      total_unread_doctors: 0,
      total_unread_nutritionists: 0,
      total_unread_support: 0,
    };
  }
};
export const getTotalUnreadCount = async (): Promise<number> => {
  try {
    const { data } = await axiosInstance.get(`${API_V1_PREFIX}${CHAT_API_PREFIX}total-unread-count`);
    return data.total_unread_count || 0;
  } catch (error: any) {
    console.error('Error getting total unread count:', error);
    return 0;
  }
};

export interface AskYourResultsRequest {
  member_id: string;
  message?: string | null;
  image_url?: string | null;
  image_base64?: string | null;
  image_mime_type?: string | null;
}

export interface AskYourResultsResponse {
  reply?: string;
  response?: string;
  answer?: string;
  success?: boolean;
  [key: string]: unknown;
}

export interface AskYourResultsPrompts {
  rules_and_guidelines?: string;
  context_attachment?: string;
  system_prompt?: string;
  developer_prompt?: string;
  rulesAndGuidelines?: string;
  contextAttachment?: string;
  systemPrompt?: string;
  developerPrompt?: string;
  [key: string]: unknown;
}

export interface GpSnapshotPrompts {
  rules_and_guidelines?: string;
  actual_prompt?: string;
  context_attachment?: string;
  system_prompt?: string;
  developer_prompt?: string;
  rulesAndGuidelines?: string;
  actualPrompt?: string;
  contextAttachment?: string;
  systemPrompt?: string;
  developerPrompt?: string;
  [key: string]: unknown;
}

export const askYourResults = async (
  memberId: string,
  message: string,
  image?: {
    imageUrl?: string | null;
    imageBase64?: string | null;
    imageMimeType?: string | null;
  }
): Promise<AskYourResultsResponse> => {
  const payload: AskYourResultsRequest = {
    member_id: memberId,
    message,
    image_url: image?.imageUrl ?? null,
    image_base64: image?.imageBase64 ?? null,
    image_mime_type: image?.imageMimeType ?? null,
  };
  const { data } = await axiosInstance.post<AskYourResultsResponse>(
    `${API_V1_PREFIX}${CHAT_API_PREFIX}ai/ask-your-results`,
    payload
  );
  return data;
};

export const getAskYourResultsPrompts = async (): Promise<AskYourResultsPrompts> => {
  const { data } = await axiosInstance.get<AskYourResultsPrompts>(
    `${API_V1_PREFIX}${CHAT_API_PREFIX}ai/prompts`
  );
  return data;
};

export const updateAskYourResultsPrompts = async (payload: {
  rules_and_guidelines?: string;
  context_attachment?: string;
}): Promise<AskYourResultsPrompts> => {
  const { data } = await axiosInstance.put<AskYourResultsPrompts>(
    `${API_V1_PREFIX}${CHAT_API_PREFIX}ai/prompts`,
    payload
  );
  return data;
};

export interface GpSnapshotResponse {
  reply?: string;
  success?: boolean;
  [key: string]: unknown;
}

export const viewGpSnapshotSummary = async (memberId: string): Promise<GpSnapshotResponse> => {
  const { data } = await axiosInstance.post<GpSnapshotResponse>(
    `${API_V1_PREFIX}${CHAT_API_PREFIX}ai/gp-snapshot/view-gp-summary`,
    { member_id: memberId }
  );
  return data;
};

export const getGpSnapshotPrompts = async (): Promise<GpSnapshotPrompts> => {
  const { data } = await axiosInstance.get<GpSnapshotPrompts>(
    `${API_V1_PREFIX}${CHAT_API_PREFIX}ai/gp-snapshot/prompts`
  );
  return data;
};

export const updateGpSnapshotPrompts = async (payload: {
  rules_and_guidelines?: string;
  actual_prompt?: string;
  context_attachment?: string;
}): Promise<GpSnapshotPrompts> => {
  const { data } = await axiosInstance.put<GpSnapshotPrompts>(
    `${API_V1_PREFIX}${CHAT_API_PREFIX}ai/gp-snapshot/prompts`,
    payload
  );
  return data;
};

export interface PersonalisedMonitoringPlanPrompts {
  rules_and_guidelines?: string;
  actual_prompt?: string;
  context_attachment?: string;
  system_prompt?: string;
  developer_prompt?: string;
  rulesAndGuidelines?: string;
  actualPrompt?: string;
  contextAttachment?: string;
  systemPrompt?: string;
  developerPrompt?: string;
  [key: string]: unknown;
}

export interface PersonalisedMonitoringPlanResponse {
  reply?: string;
  success?: boolean;
  [key: string]: unknown;
}

export const generatePersonalisedMonitoringPlan = async (
  memberId: string
): Promise<PersonalisedMonitoringPlanResponse> => {
  const { data } = await axiosInstance.post<PersonalisedMonitoringPlanResponse>(
    `${API_V1_PREFIX}${CHAT_API_PREFIX}ai/personalised-monitoring-plan/generate`,
    { member_id: memberId }
  );
  return data;
};

export const getPersonalisedMonitoringPlanPrompts =
  async (): Promise<PersonalisedMonitoringPlanPrompts> => {
    const { data } = await axiosInstance.get<PersonalisedMonitoringPlanPrompts>(
      `${API_V1_PREFIX}${CHAT_API_PREFIX}ai/personalised-monitoring-plan/prompts`
    );
    return data;
  };

export const updatePersonalisedMonitoringPlanPrompts = async (payload: {
  rules_and_guidelines?: string;
  actual_prompt?: string;
  context_attachment?: string;
}): Promise<PersonalisedMonitoringPlanPrompts> => {
  const { data } = await axiosInstance.put<PersonalisedMonitoringPlanPrompts>(
    `${API_V1_PREFIX}${CHAT_API_PREFIX}ai/personalised-monitoring-plan/prompts`,
    payload
  );
  return data;
};

export interface AiModel {
  key: string;
  name: string;
  provider: string;
  model_name: string;
  model_version: string;
  deployment_name: string;
  format: string;
  sku_name: string;
  capacity: number;
  active: boolean;
  default: boolean;
}

export interface ListAiModelsResponse {
  models: AiModel[];
  default_model_key?: string | null;
  default_model_name?: string | null;
}

export interface RestrictedChatResponse {
  reply: string;
  success: boolean;
  model_key: string;
  deployment_name: string;
}

export interface RestrictedChatPrompts {
  rules_and_guidelines?: string;
  context_attachment?: string;
  system_prompt?: string;
  developer_prompt?: string;
  rulesAndGuidelines?: string;
  contextAttachment?: string;
  systemPrompt?: string;
  developerPrompt?: string;
  [key: string]: unknown;
}

export const listAiModels = async (): Promise<ListAiModelsResponse> => {
  const { data } = await axiosInstance.get<ListAiModelsResponse>(
    `${API_V1_PREFIX}${CHAT_API_PREFIX}ai/models`
  );
  return data;
};

export interface BodySystemAnalysisSettings {
  model_key?: string | null;
  model_name?: string | null;
  deployment_name?: string | null;
  using_default?: boolean;
}

export const getBodySystemAnalysisSettings =
  async (): Promise<BodySystemAnalysisSettings> => {
    const { data } = await axiosInstance.get<BodySystemAnalysisSettings>(
      `${API_V1_PREFIX}${CHAT_API_PREFIX}ai/body-system-analysis/settings`
    );
    return data;
  };

export const updateBodySystemAnalysisSettings = async (payload: {
  model_key?: string | null;
}): Promise<BodySystemAnalysisSettings> => {
  const { data } = await axiosInstance.put<BodySystemAnalysisSettings>(
    `${API_V1_PREFIX}${CHAT_API_PREFIX}ai/body-system-analysis/settings`,
    payload
  );
  return data;
};

export const sendRestrictedChatMessage = async (
  memberId: string,
  message: string,
  model?: string
): Promise<RestrictedChatResponse> => {
  const payload: { member_id: string; message: string; model?: string } = {
    member_id: memberId,
    message,
  };
  if (model) {
    payload.model = model;
  }
  const { data } = await axiosInstance.post<RestrictedChatResponse>(
    `${API_V1_PREFIX}${CHAT_API_PREFIX}ai/restricted-chat`,
    payload
  );
  return data;
};

export const getRestrictedChatPrompts = async (): Promise<RestrictedChatPrompts> => {
  const { data } = await axiosInstance.get<RestrictedChatPrompts>(
    `${API_V1_PREFIX}${CHAT_API_PREFIX}ai/restricted-chat/prompts`
  );
  return data;
};

export const updateRestrictedChatPrompts = async (payload: {
  rules_and_guidelines?: string;
  context_attachment?: string;
}): Promise<RestrictedChatPrompts> => {
  const { data } = await axiosInstance.put<RestrictedChatPrompts>(
    `${API_V1_PREFIX}${CHAT_API_PREFIX}ai/restricted-chat/prompts`,
    payload
  );
  return data;
};

const chatApi = {
  connectUser,
  disconnectUser,
  getOrCreateChannel,
  queryChannels,
  markChannelAsRead,
  getUnreadCount,
  STREAM_API_KEY,
  // Backend API methods
  getChatToken,
  getMemberChats,
  getDoctorChats,
  getNutritionistChats,
  getAdminChats,
  getUnreadCounts,
  getTotalUnreadCount,
  askYourResults,
  getAskYourResultsPrompts,
  updateAskYourResultsPrompts,
  viewGpSnapshotSummary,
  getGpSnapshotPrompts,
  updateGpSnapshotPrompts,
  generatePersonalisedMonitoringPlan,
  getPersonalisedMonitoringPlanPrompts,
  updatePersonalisedMonitoringPlanPrompts,
  listAiModels,
  getBodySystemAnalysisSettings,
  updateBodySystemAnalysisSettings,
  sendRestrictedChatMessage,
  getRestrictedChatPrompts,
  updateRestrictedChatPrompts,
};

export default chatApi;

